/**
 * Centralized, typed access to public runtime config. NEXT_PUBLIC_* values are
 * inlined at build time. Keep all env reads here so missing config surfaces in
 * one place rather than scattered `process.env` lookups.
 */
function required(value: string | undefined, name: string): string {
  if (!value) {
    // In dev/test we fall back to a sentinel so the app still boots against MSW.
    if (process.env.NODE_ENV !== "production") {
      return value ?? `http://localhost:0/__missing__/${name}`;
    }
    throw new Error(`Missing required env var: ${name}`);
  }
  return value;
}

export const env = {
  /** Bare backend origin. The HTTP client appends /api/v1, /login, etc. */
  backendUrl: required(process.env.NEXT_PUBLIC_BACKEND, "NEXT_PUBLIC_BACKEND"),
  /** Socket.IO origin (path is /xgodo.socket.io). */
  backendSocketUrl:
    process.env.NEXT_PUBLIC_BACKEND_SOCKET ?? "http://localhost:0",
  /**
   * Public marketing origin used to build referral links, e.g.
   * `https://xgodo.com?ref=<id>`. Deliberately NOT the app origin (which on the
   * dev/preview box is a raw IP:port) — the shared link should always read as
   * the production domain. Override per environment; defaults to the prod root.
   */
  referralBaseUrl:
    process.env.NEXT_PUBLIC_REFERRAL_BASE_URL ?? "https://xgodo.com",
  rmsApiUrl: process.env.NEXT_PUBLIC_RMS_API_URL ?? "",
  rmsApiPublicUrl: process.env.NEXT_PUBLIC_RMS_API_PUBLIC_URL ?? "",
  appPackageName:
    process.env.NEXT_PUBLIC_APP_PACKAGE_NAME ?? "in.omdev.remotemobile",
  /**
   * Origin that serves the built APKs under `/app/<file_name>`. Only the
   * `xgodo.com` host has that path mounted — it is NOT served by this app's
   * origin, so `/download` must redirect to an absolute URL here rather than a
   * relative `/app/...` (which is what the legacy handler did, on the one host
   * where both lived together). Point this at whichever origin serves the APK
   * directory; once `/app/*` is mounted on the app host too, set it to "" to
   * fall back to a same-origin relative redirect.
   */
  appDownloadBaseUrl:
    process.env.NEXT_PUBLIC_APP_DOWNLOAD_BASE_URL ?? "https://xgodo.com",
  logLevel: (process.env.NEXT_PUBLIC_LOG_LEVEL ?? "info") as
    "debug" | "info" | "warn" | "error" | "silent",
  sentryDsn: process.env.NEXT_PUBLIC_SENTRY_DSN ?? "",
  /**
   * Build-time default for the demo/dummy-data scenario served by MSW. The raw
   * string; `@/lib/demo-config` validates it and layers runtime overrides on
   * top. Only consulted when API mocking is enabled — it has no effect against
   * a real backend.
   */
  demoScenario: process.env.NEXT_PUBLIC_DEMO_SCENARIO ?? "",
  /**
   * Build-time default data source for the device Marketplace ("mock" | "live").
   * `@/lib/demo-config` validates it and layers the `?marketplace=` runtime
   * override on top. Only consulted when API mocking is enabled; `"live"` makes
   * the marketplace handler pass through to `NEXT_PUBLIC_BACKEND` while every
   * other endpoint stays mocked.
   */
  marketplaceSource: process.env.NEXT_PUBLIC_MARKETPLACE_SOURCE ?? "",
  /**
   * When `"enabled"`, MSW passes the auth (login) and device-list reads through
   * to the REAL `NEXT_PUBLIC_BACKEND` while every other endpoint stays mocked.
   * Required for live device control: the signaling server rejects a mock JWT
   * and knows nothing of the mock device fixtures, so live control needs a real
   * token + real device ids. Set only where `NEXT_PUBLIC_BACKEND` is a real
   * backend (the dev server); never on the mock-backed :3056 build.
   */
  liveBackend: process.env.NEXT_PUBLIC_LIVE_BACKEND ?? "",
  /**
   * Percentage of the device rollout buckets (0–99) for which a per-action
   * market listing additionally requires a verified phone number. `0` (the
   * default) disables the requirement. Mirrors the legacy
   * `NEXT_PUBLIC_PHONE_ROLLOUT_PERCENT`, which the market form reads to warn
   * about devices the backend would reject.
   */
  phoneRolloutPercent:
    Number.parseInt(process.env.NEXT_PUBLIC_PHONE_ROLLOUT_PERCENT ?? "0", 10) ||
    0,
} as const;
